import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import PreferenceForm from '@/components/PreferenceForm';
import { api } from '@/lib/api';
import { PREFS } from '@/constants/testIds';
import { toast } from 'sonner';
import { ListChecks, Pencil, Check, X } from 'lucide-react';

function WelcomeBanner({ tripName, onDismiss }) {
    return (
        <div
            data-testid="preferences-welcome-banner"
            className="ns-card mt-6 mb-4 p-4 sm:p-5 flex items-start gap-3 rounded-3xl"
            style={{ background: 'rgba(196,85,45,0.08)', borderColor: 'rgba(196,85,45,0.25)' }}
        >
            <span className="text-2xl leading-none flex-shrink-0" aria-hidden="true">👋</span>
            <div className="flex-1 min-w-0">
                <p className="text-sm sm:text-[15px] text-[var(--ns-text)] leading-relaxed">
                    <strong>Welcome!</strong> You&rsquo;ve joined <strong>{tripName}</strong>. Fill in your
                    preferences below so the group can generate a fair itinerary together.
                </p>
            </div>
            <button
                type="button"
                onClick={onDismiss}
                aria-label="Dismiss welcome banner"
                data-testid="preferences-welcome-banner-dismiss"
                className="flex-shrink-0 w-8 h-8 rounded-full text-[var(--ns-text3)] hover:text-[var(--ns-text)] hover:bg-[var(--ns-soft)] flex items-center justify-center transition"
            >
                <X size={16} />
            </button>
        </div>
    );
}

export default function PreferencesTab({ tripId, trip, currentUserId, onSubmitted, showWelcomeBanner, onDismissWelcome }) {
    const [loading, setLoading] = useState(true);
    const [existing, setExisting] = useState(null);
    const [editing, setEditing] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/trips/${tripId}/preferences/me`);
            setExisting(data.preferences);
            if (!data.preferences) setEditing(true);
        } catch { /* ignore */ }
        finally { setLoading(false); }
    }, [tripId]);

    useEffect(() => { load(); }, [load]);

    const submit = async (payload) => {
        setSubmitting(true);
        try {
            const { data } = await api.post(`/trips/${tripId}/preferences`, payload);
            setExisting(data.preferences);
            setEditing(false);
            toast.success('Preferences saved!');
            onDismissWelcome?.();
            onSubmitted?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not save');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return <div className="ns-card p-12 text-center text-[var(--ns-text2)] mt-6">Loading your preferences…</div>;
    }

    const total = trip.participants?.length || 1;
    const submitted = trip.preferences_submitted || 0;
    const waiting = Math.max(0, total - submitted);
    const banner = showWelcomeBanner ? (
        <WelcomeBanner tripName={trip.name || 'this trip'} onDismiss={() => onDismissWelcome?.()} />
    ) : null;

    if (editing) {
        return (
            <div data-testid={PREFS.intro}>
                {banner}
                <div className="mt-2" onFocusCapture={() => onDismissWelcome?.()}>
                    <PreferenceForm
                        initial={existing}
                        onSubmit={submit}
                        submitting={submitting}
                        onCancel={() => existing ? setEditing(false) : null}
                    />
                </div>
            </div>
        );
    }

    // Already submitted view
    return (
        <div data-testid={PREFS.submittedBanner}>
            {banner}
            <div className="ns-card p-8 sm:p-10 mt-2">
                <div className="flex items-start gap-4">
                    <span className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(74,124,89,0.12)' }}>
                        <Check className="text-[var(--ns-success)]" />
                    </span>
                    <div className="flex-1">
                        <h3 className="font-display text-2xl font-semibold tracking-tight">Preferences saved!</h3>
                        <p className="text-[var(--ns-text2)] mt-2 leading-relaxed">
                            {waiting === 0
                                ? "Everyone has submitted — your Group Profile is ready."
                                : `Waiting for ${waiting} more ${waiting === 1 ? 'participant' : 'participants'} to submit.`}
                        </p>
                        <div className="flex flex-wrap gap-3 mt-5">
                            <Button
                                onClick={() => setEditing(true)}
                                data-testid={PREFS.editButton}
                                variant="outline"
                                className="rounded-full h-11 px-5 border-[var(--ns-border)]"
                            >
                                <Pencil size={14} className="mr-2" /> Edit my preferences
                            </Button>
                            {trip.participants?.length >= 2 && submitted >= 2 && (
                                <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[var(--ns-text3)] self-center">
                                    <ListChecks size={12} /> Group profile available
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
